/**
 * FileRefInput — Chat input with @-autocomplete, attachments, and queue support
 *
 * Key UX: input is NEVER disabled during generation. Users type freely,
 * messages get queued and auto-fire as continuation after current response.
 *
 * Attachment support:
 * - + button → file picker (images + any file)
 * - Ctrl/Cmd+V → paste image from clipboard
 * - Drag-and-drop → drop zone on entire input area
 * - Thumbnail strip above textarea
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import type { FileStat } from '../../engine/types'

/** Pending attachment before send */
export interface PendingAttachment {
    id: string
    file: File
    name: string
    mime: string
    size: number
    previewUrl: string | null  // blob: URL for images, null for others
}

interface FileRefInputProps {
    onSend: (text: string, attachments: PendingAttachment[]) => void
    onStop?: () => void
    isRunning: boolean
    isStopping: boolean
    files: FileStat[]
    queueLength: number
    focusRequestKey?: number
}

const IMAGE_MIMES = new Set([
    'image/png', 'image/jpeg', 'image/gif', 'image/webp',
    'image/svg+xml', 'image/bmp', 'image/x-icon',
])

let nextAttachmentId = 1

export function FileRefInput({ onSend, onStop, isRunning, isStopping, files, queueLength, focusRequestKey = 0 }: FileRefInputProps) {
    const [text, setText] = useState('')
    const [showAutocomplete, setShowAutocomplete] = useState(false)
    const [acFilter, setAcFilter] = useState('')
    const [acIndex, setAcIndex] = useState(0)
    const [attachments, setAttachments] = useState<PendingAttachment[]>([])
    const [isDragOver, setIsDragOver] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Fuzzy-filter files for autocomplete
    const filtered = acFilter
        ? files.filter(f => {
            const query = acFilter.toLowerCase()
            const path = f.path.toLowerCase()
            const basename = path.split('/').pop() ?? ''
            let qi = 0
            for (let pi = 0; pi < path.length && qi < query.length; pi++) {
                if (path[pi] === query[qi]) qi++
            }
            if (qi === query.length) return true
            return basename.includes(query)
        }).slice(0, 10)
        : files.slice(0, 10)

    // Auto-resize textarea
    useEffect(() => {
        const el = textareaRef.current
        if (!el) return
        el.style.height = 'auto'
        el.style.height = Math.min(el.scrollHeight, 120) + 'px'
    }, [text])

    useEffect(() => {
        const el = textareaRef.current
        if (!el) return
        el.focus({ preventScroll: true })
        const cursor = el.value.length
        el.setSelectionRange(cursor, cursor)
    }, [focusRequestKey])

    // ── Attachment helpers ─────────────────────────────────────
    const addFiles = useCallback((fileList: FileList | File[]) => {
        const newAttachments: PendingAttachment[] = []
        for (const file of fileList) {
            const isImage = IMAGE_MIMES.has(file.type)
            newAttachments.push({
                id: `att-${nextAttachmentId++}`,
                file,
                name: file.name,
                mime: file.type || 'application/octet-stream',
                size: file.size,
                previewUrl: isImage ? URL.createObjectURL(file) : null,
            })
        }
        setAttachments(prev => [...prev, ...newAttachments])
    }, [])

    const removeAttachment = useCallback((id: string) => {
        setAttachments(prev => {
            const att = prev.find(a => a.id === id)
            if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl)
            return prev.filter(a => a.id !== id)
        })
    }, [])

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            attachments.forEach(a => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl) })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ── Paste handler (images from clipboard) ─────────────────
    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items
        if (!items) return
        const imageFiles: File[] = []
        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (item.kind === 'file' && item.type.startsWith('image/')) {
                const file = item.getAsFile()
                if (file) imageFiles.push(file)
            }
        }
        if (imageFiles.length > 0) {
            e.preventDefault()
            addFiles(imageFiles)
        }
    }, [addFiles])

    // ── Drag & drop ───────────────────────────────────────────
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(true)
    }, [])

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
    }, [])

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
        if (e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files)
        }
    }, [addFiles])

    // ── Text input handling ───────────────────────────────────
    const handleInput = useCallback((value: string) => {
        setText(value)
        const cursor = textareaRef.current?.selectionStart ?? value.length
        const before = value.slice(0, cursor)
        const match = before.match(/@([^\s]*)$/)
        if (match) {
            setShowAutocomplete(true)
            setAcFilter(match[1])
            setAcIndex(0)
        } else {
            setShowAutocomplete(false)
        }
    }, [])

    const insertRef = useCallback((path: string) => {
        const cursor = textareaRef.current?.selectionStart ?? text.length
        const before = text.slice(0, cursor)
        const afterCursor = text.slice(cursor)
        const refStart = before.lastIndexOf('@')
        const newText = before.slice(0, refStart) + '@' + path + ' ' + afterCursor
        setText(newText)
        setShowAutocomplete(false)
        textareaRef.current?.focus()
    }, [text])

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (showAutocomplete) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setAcIndex(i => Math.min(i + 1, filtered.length - 1)) }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setAcIndex(i => Math.max(i - 1, 0)) }
            else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault()
                if (filtered[acIndex]) insertRef(filtered[acIndex].path)
            }
            else if (e.key === 'Escape') { e.preventDefault(); setShowAutocomplete(false) }
            return
        }

        // Enter sends (Shift+Enter for newline)
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }, [showAutocomplete, acIndex, filtered, insertRef, text, attachments])

    const handleSend = useCallback(() => {
        if (text.trim() || attachments.length > 0) {
            onSend(text.trim(), attachments)
            setText('')
            setAttachments([])
        }
    }, [text, attachments, onSend])

    const hasDraft = text.trim().length > 0 || attachments.length > 0

    return (
        <div
            className={`agent-input-area ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {/* Queue indicator */}
            {queueLength > 0 && (
                <div className="queue-indicator">
                    <span className="queue-dot" />
                    {queueLength} queued and ready to send next
                </div>
            )}

            {/* Attachment strip */}
            {attachments.length > 0 && (
                <div className="attachment-strip">
                    {attachments.map(att => (
                        <div key={att.id} className="attachment-thumb">
                            {att.previewUrl ? (
                                <img
                                    src={att.previewUrl}
                                    alt={att.name}
                                    className="attachment-preview"
                                />
                            ) : (
                                <div className="attachment-file-icon">
                                    <span className="file-icon-emoji">📎</span>
                                    <span className="file-icon-name">{att.name}</span>
                                </div>
                            )}
                            <button
                                className="attachment-remove"
                                onClick={() => removeAttachment(att.id)}
                                title="Remove attachment"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="agent-input-row">
                {/* Autocomplete dropdown */}
                {showAutocomplete && filtered.length > 0 && (
                    <div className="autocomplete-dropdown">
                        {filtered.map((f, i) => (
                            <button
                                key={f.path}
                                className={`ac-item ${i === acIndex ? 'ac-active' : ''}`}
                                onClick={() => insertRef(f.path)}
                                onMouseEnter={() => setAcIndex(i)}
                            >
                                <span className="ac-icon">📄</span>
                                <span className="ac-path">{f.path}</span>
                                <span className="ac-size">{f.size}b</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* + button for file picker */}
                <button
                    className="btn agent-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach files or images"
                >
                    +
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden-file-input"
                    onChange={e => {
                        if (e.target.files) addFiles(e.target.files)
                        e.target.value = ''  // allow re-selecting same file
                    }}
                />

                <textarea
                    ref={textareaRef}
                    className="agent-textarea"
                    value={text}
                    onChange={e => handleInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={isRunning ? 'Type to queue next message…' : 'Type a message…'}
                    rows={1}
                    enterKeyHint={isRunning ? 'done' : 'send'}
                    aria-label="Message input"
                    autoFocus
                />

                <div className="agent-input-actions">
                    {isStopping && !hasDraft ? (
                        <button
                            className="btn agent-stop-btn"
                            onClick={onStop}
                            disabled
                            title="Stopping generation"
                        >
                            <span className="stop-icon">■</span>
                            Stopping
                        </button>
                    ) : (isRunning || isStopping) && hasDraft ? (
                        <button
                            className="btn btn-primary agent-send-btn"
                            onClick={handleSend}
                            title="Queue message"
                        >
                            Queue
                        </button>
                    ) : isRunning ? (
                        <button
                            className="btn agent-stop-btn"
                            onClick={onStop}
                            title="Stop generation"
                        >
                            <span className="stop-icon">■</span>
                            Stop
                        </button>
                    ) : (
                        <button
                            className="btn btn-primary agent-send-btn"
                            onClick={handleSend}
                            disabled={!text.trim() && attachments.length === 0}
                            title="Send message"
                        >
                            Send
                        </button>
                    )}
                </div>
            </div>

            {/* Drag overlay */}
            {isDragOver && (
                <div className="drag-overlay">
                    <span>Drop files here</span>
                </div>
            )}
        </div>
    )
}
